import { useRef, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { RewardsEventController } from '@leagueplatform/rewards-events';
import { BasePage } from 'components/base-page/base-page.component';
import { OnboardingFeatureHighlights } from 'components/onboarding-feature-highlights/onboarding-feature-highlights.component';
import { ConnectionErrorPage } from 'pages/errors/connection-error/connection-error.page';
import { AccountSetupPage } from 'pages/onboarding/account-setup/account-setup.page';
import { ConsentPage } from 'pages/onboarding/consent.page';
import { CaregiverPermissionsPage } from 'pages/onboarding/caregiver-permissions/caregiver-permissions.page';
import { useOnboarding } from 'hooks/use-onboarding.hook';

export const PrivateRoutesContainer = () => {
  const {
    shouldShowOnboarding,
    isLoading,
    hasError,
    retry,
    step,
    isCaregiver,
    onOnboardingComplete,
    onAccountSetupComplete,
    onConsentComplete,
  } = useOnboarding();

  const stepContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldShowOnboarding) {
      stepContainerRef.current?.focus();
    }
  }, [step, shouldShowOnboarding]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (hasError) {
    return <ConnectionErrorPage retry={retry} />;
  }

  if (shouldShowOnboarding) {
    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      <div
        ref={stepContainerRef}
        tabIndex={-1}
        style={{ outline: '2px solid transparent' }}
      >
        {step === 'highlights' && (
          <OnboardingFeatureHighlights
            onOnboardingComplete={onOnboardingComplete}
          />
        )}
        {step === 'account-setup' && (
          <AccountSetupPage
            onComplete={onAccountSetupComplete}
            isCaregiver={isCaregiver}
          />
        )}
        {step === 'consent' && (
          <ConsentPage
            onComplete={isCaregiver ? onConsentComplete : undefined}
          />
        )}
        {step === 'caregiver-permissions' && <CaregiverPermissionsPage />}
      </div>
    );
  }

  return (
    <BasePage>
      <Outlet />
      <RewardsEventController />
    </BasePage>
  );
};
